<script setup>
import { ref, computed, onMounted } from 'vue';
import { Head, Link, usePage } from '@inertiajs/inertia-vue3';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import SectionMain from '@/Components/SectionMain.vue';
import CardBox from '@/Components/CardBox.vue';
import BaseIcon from '@/Components/BaseIcon.vue';
import {
    mdiShieldKey,
    mdiLock,
    mdiLockOpenVariant,
    mdiCloudUpload,
    mdiHistory,
    mdiRefresh,
    mdiCog,
    mdiCheckCircle,
    mdiAlertCircle,
    mdiCloseCircle,
    mdiArrowLeft,
    mdiServerNetwork,
    mdiDatabaseSync,
    mdiBroom,
    mdiCodeBrackets,
    mdiAccountMultiple,
    mdiInformationOutline
} from '@mdi/js';

const props = defineProps({
    constantes: {
        type: Array,
        default: () => []
    },
    appVersion: {
        type: String,
        default: '18.0.0'
    },
    updateHistory: {
        type: Array,
        default: () => []
    },
    phpVersion: {
        type: String,
        default: ''
    },
    laravelVersion: {
        type: String,
        default: ''
    },
    osInfo: {
        type: String,
        default: ''
    }
});

const page = usePage();
const confGlobal = computed(() => page.props.value.confGlobal || {});

// Estados generales
const activeTab = ref('actualizacion');
const pinInput = ref('');
const isUnlocked = ref(false);
const pinError = ref('');
const savingKeys = ref({});
const successMsg = ref('');
const errorMsg = ref('');

// Lista reactiva de constantes
const localConstants = ref([]);

onMounted(() => {
    localConstants.value = (props.constantes || []).map(item => ({ ...item }));
});

const correctPin = computed(() => {
    return confGlobal.value.CONSTANT_PIN_SECURITY_MODIFY_SUPER_ADMIN
        || confGlobal.value.CONSTANT_PIN_SECURITY_MODIFY
        || '130116';
});

const verifyPin = () => {
    if (pinInput.value === correctPin.value) {
        isUnlocked.value = true;
        pinError.value = '';
    } else {
        pinError.value = 'PIN de seguridad de SuperAdmin incorrecto.';
    }
};

const getConstant = (name) => {
    return localConstants.value.find(c => c.name === name);
};

const getConstantValue = (name) => {
    const found = getConstant(name);
    return found ? found.value : '';
};

const updateConstant = async (name, newValue) => {
    if (!isUnlocked.value) {
        errorMsg.value = 'Debe ingresar el PIN de seguridad para modificar configuraciones.';
        return;
    }

    savingKeys.value[name] = true;
    errorMsg.value = '';
    successMsg.value = '';

    try {
        const response = await axios.post(route('control-panel.update-constant'), {
            name: name,
            value: newValue,
            pin_security: pinInput.value
        });

        if (response.data.success) {
            const updated = response.data.constant;
            const target = localConstants.value.find(c => c.name === name);
            if (target) {
                target.value = updated.value;
                target.updated_at = updated.updated_at;
                target.user_name = updated.user_name;
            } else {
                localConstants.value.push({ ...updated });
            }

            successMsg.value = response.data.message || 'Constante actualizada con éxito.';
            setTimeout(() => { successMsg.value = ''; }, 3000);
        }
    } catch (err) {
        console.error(err);
        errorMsg.value = err.response?.data?.message || 'Error al actualizar constante.';
        setTimeout(() => { errorMsg.value = ''; }, 4000);
    } finally {
        savingKeys.value[name] = false;
    }
};

// ==========================================
// PESTAÑA 1: ACTUALIZACIÓN DEL SISTEMA
// ==========================================
const selectedZipFile = ref(null);
const fileInputRef = ref(null);
const isUpdating = ref(false);
const updateLogs = ref([]);
const updateSuccess = ref(false);
const updateError = ref('');
const isCheckingRemote = ref(false);
const remoteCheckResult = ref(null);
const localUpdateHistory = ref([...(props.updateHistory || [])]);
const currentSystemVersion = ref(props.appVersion || '18.0.0');

const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
        if (!file.name.endsWith('.zip')) {
            errorMsg.value = 'El archivo seleccionado debe tener extensión .zip';
            selectedZipFile.value = null;
            return;
        }
        selectedZipFile.value = file;
        updateLogs.value = [];
        updateError.value = '';
    }
};

const triggerFileInput = () => {
    fileInputRef.value?.click();
};

const executeZipUpload = async () => {
    if (!isUnlocked.value) {
        errorMsg.value = 'Debe ingresar el PIN de seguridad para instalar actualizaciones.';
        return;
    }
    if (!selectedZipFile.value) {
        errorMsg.value = 'Debe seleccionar un archivo ZIP de actualización.';
        return;
    }

    if (!confirm(`¿Está seguro de instalar la actualización desde "${selectedZipFile.value.name}"? El sistema procesará los archivos y ejecutará migraciones.`)) {
        return;
    }

    isUpdating.value = true;
    updateLogs.value = [];
    updateError.value = '';
    updateSuccess.value = false;

    const formData = new FormData();
    formData.append('zip_file', selectedZipFile.value);
    formData.append('update_file', selectedZipFile.value);
    formData.append('pin_security', pinInput.value);

    try {
        const response = await axios.post(route('control-panel.upload-update'), formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        if (response.data.success) {
            updateSuccess.value = true;
            updateLogs.value = response.data.log || [];
            if (response.data.new_version) {
                currentSystemVersion.value = response.data.new_version;
            }
            if (response.data.history_item) {
                localUpdateHistory.value.unshift(response.data.history_item);
            }
            successMsg.value = response.data.message || 'Actualización instalada con éxito.';
            selectedZipFile.value = null;
            if (fileInputRef.value) fileInputRef.value.value = '';
        } else {
            updateError.value = response.data.error || response.data.message || 'Ocurrió un error durante la actualización.';
            updateLogs.value = response.data.log || [];
        }
    } catch (err) {
        console.error(err);
        updateError.value = err.response?.data?.error || err.response?.data?.message || err.message || 'Error en la petición de actualización.';
    } finally {
        isUpdating.value = false;
    }
};

const checkRemoteERP = async () => {
    if (!isUnlocked.value) {
        errorMsg.value = 'Debe ingresar el PIN de seguridad para consultar al ERP.';
        return;
    }
    isCheckingRemote.value = true;
    remoteCheckResult.value = null;
    try {
        const response = await axios.post(route('control-panel.check-remote-update'), {
            pin_security: pinInput.value
        });
        remoteCheckResult.value = response.data;
    } catch (err) {
        console.error(err);
        remoteCheckResult.value = {
            success: false,
            message: err.response?.data?.message || 'No fue posible conectar con el servidor central ERP.'
        };
    } finally {
        isCheckingRemote.value = false;
    }
};

const installRemoteVersion = async (url) => {
    if (!isUnlocked.value) {
        errorMsg.value = 'Debe ingresar el PIN de seguridad para instalar.';
        return;
    }
    if (!confirm('¿Desea descargar e instalar esta actualización desde el servidor central?')) {
        return;
    }
    isUpdating.value = true;
    updateLogs.value = [];
    updateError.value = '';
    updateSuccess.value = false;

    try {
        const response = await axios.post(route('control-panel.upload-update'), {
            update_url: url,
            pin_security: pinInput.value
        });

        if (response.data.success) {
            updateSuccess.value = true;
            updateLogs.value = response.data.log || [];
            if (response.data.new_version) {
                currentSystemVersion.value = response.data.new_version;
            }
            if (response.data.history_item) {
                localUpdateHistory.value.unshift(response.data.history_item);
            }
            successMsg.value = response.data.message || 'Actualización instalada con éxito.';
            remoteCheckResult.value = null;
        } else {
            updateError.value = response.data.error || response.data.message || 'Ocurrió un error durante la actualización.';
            updateLogs.value = response.data.log || [];
        }
    } catch (err) {
        console.error(err);
        updateError.value = err.response?.data?.error || err.response?.data?.message || err.message || 'Error en la petición de actualización.';
    } finally {
        isUpdating.value = false;
    }
};

// ==========================================
// PESTAÑA 3: MANTENIMIENTO Y CACHÉ
// ==========================================
const isClearingCache = ref(false);
const cacheOutput = ref('');

const clearCache = async () => {
    if (!isUnlocked.value) {
        errorMsg.value = 'Debe ingresar el PIN de seguridad para ejecutar mantenimiento.';
        return;
    }
    isClearingCache.value = true;
    cacheOutput.value = '';
    try {
        const response = await axios.post(route('control-panel.clear-cache'), {
            pin_security: pinInput.value
        });
        if (response.data.success) {
            cacheOutput.value = response.data.output || response.data.message;
            successMsg.value = response.data.message;
            setTimeout(() => { successMsg.value = ''; }, 3000);
        } else {
            errorMsg.value = response.data.message;
        }
    } catch (err) {
        console.error(err);
        errorMsg.value = err.response?.data?.message || 'Error al ejecutar limpieza de caché.';
    } finally {
        isClearingCache.value = false;
    }
};
</script>

<template>
    <Head title="Panel SuperAdmin - Desarrollador" />

    <BreezeAuthenticatedLayout>
        <template #header>
            <PremiumPageHeader
                title="Panel de Control SuperAdmin"
                subtitle="Herramientas de Desarrollador, Despliegues, Conexión ERP y Mantenimiento del Core"
            >
                <div class="flex items-center gap-2">
                    <Link
                        :href="route('control-panel')"
                        class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition shadow-xs"
                    >
                        <BaseIcon :path="mdiAccountMultiple" :size="16" />
                        <span>Panel de Clientes</span>
                    </Link>
                    <Link
                        :href="route('config')"
                        class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition shadow-sm"
                    >
                        <BaseIcon :path="mdiArrowLeft" :size="16" />
                        <span>Configuración</span>
                    </Link>
                </div>
            </PremiumPageHeader>
        </template>

        <SectionMain class="!p-0 space-y-6 max-w-7xl mx-auto">
            <!-- Barra de Seguridad PIN -->
            <div class="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 transition-all">
                <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div class="flex items-center gap-3">
                        <div
                            :class="isUnlocked ? 'bg-purple-100 text-purple-600 ring-4 ring-purple-50' : 'bg-amber-100 text-amber-600 ring-4 ring-amber-50'"
                            class="w-12 h-12 rounded-xl flex items-center justify-center transition shrink-0"
                        >
                            <BaseIcon :path="isUnlocked ? mdiLockOpenVariant : mdiShieldKey" :size="28" />
                        </div>
                        <div>
                            <h3 class="text-base font-bold text-slate-800">
                                {{ isUnlocked ? 'Modo SuperAdmin Desbloqueado' : 'Seguridad: Ingrese PIN de SuperAdmin' }}
                            </h3>
                            <p class="text-xs text-slate-500">
                                {{ isUnlocked ? 'Tiene permisos activos para desplegar actualizaciones, editar constantes y realizar mantenimiento.' : 'Ingrese el PIN de SuperAdmin para autorizar cambios de bajo nivel.' }}
                            </p>
                        </div>
                    </div>

                    <!-- Input PIN -->
                    <div class="flex items-center gap-2 w-full md:w-auto">
                        <template v-if="!isUnlocked">
                            <input
                                v-model="pinInput"
                                type="text"
                                name="superadmin_pin_input"
                                id="superadmin_pin_input"
                                autocomplete="off"
                                autocorrect="off"
                                autocapitalize="off"
                                spellcheck="false"
                                data-lpignore="true"
                                data-1p-ignore
                                style="-webkit-text-security: disc; text-security: disc;"
                                placeholder="PIN SuperAdmin"
                                class="px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-full md:w-44 font-mono tracking-widest"
                                @keyup.enter="verifyPin"
                            />
                            <button
                                type="button"
                                @click="verifyPin"
                                class="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-xl shadow-sm transition whitespace-nowrap"
                            >
                                Desbloquear
                            </button>
                        </template>
                        <template v-else>
                            <span class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                                <BaseIcon :path="mdiCheckCircle" :size="16" />
                                SuperAdmin Activo
                            </span>
                            <button
                                type="button"
                                @click="isUnlocked = false; pinInput = ''"
                                class="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-200 rounded-xl hover:bg-slate-50"
                            >
                                Bloquear
                            </button>
                        </template>
                    </div>
                </div>

                <div v-if="pinError" class="mt-3 text-xs text-rose-600 flex items-center gap-1">
                    <BaseIcon :path="mdiAlertCircle" :size="15" />
                    <span>{{ pinError }}</span>
                </div>
            </div>

            <!-- Alertas de éxito / error -->
            <div v-if="successMsg" class="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2">
                <BaseIcon :path="mdiCheckCircle" :size="18" />
                <span>{{ successMsg }}</span>
            </div>
            <div v-if="errorMsg" class="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm flex items-center gap-2">
                <BaseIcon :path="mdiCloseCircle" :size="18" />
                <span>{{ errorMsg }}</span>
            </div>

            <!-- Navegación de Tabs -->
            <div class="flex border-b border-slate-200 bg-white rounded-t-2xl px-4 pt-2 gap-2 overflow-x-auto">
                <button
                    type="button"
                    @click="activeTab = 'actualizacion'"
                    :class="activeTab === 'actualizacion' ? 'border-purple-600 text-purple-700 bg-purple-50/50' : 'border-transparent text-slate-600 hover:text-slate-900'"
                    class="flex items-center gap-2 px-4 py-3 border-b-2 font-semibold text-sm rounded-t-xl transition whitespace-nowrap"
                >
                    <BaseIcon :path="mdiCloudUpload" :size="20" class="text-purple-600" />
                    <span>1. Actualización y Despliegue</span>
                    <span class="ml-1 text-[11px] font-mono px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-bold">
                        v{{ currentSystemVersion }}
                    </span>
                </button>
                <button
                    type="button"
                    @click="activeTab = 'constantes'"
                    :class="activeTab === 'constantes' ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50' : 'border-transparent text-slate-600 hover:text-slate-900'"
                    class="flex items-center gap-2 px-4 py-3 border-b-2 font-semibold text-sm rounded-t-xl transition whitespace-nowrap"
                >
                    <BaseIcon :path="mdiCog" :size="20" class="text-indigo-600" />
                    <span>2. Parámetros del Core</span>
                </button>
                <button
                    type="button"
                    @click="activeTab = 'mantenimiento'"
                    :class="activeTab === 'mantenimiento' ? 'border-rose-600 text-rose-700 bg-rose-50/50' : 'border-transparent text-slate-600 hover:text-slate-900'"
                    class="flex items-center gap-2 px-4 py-3 border-b-2 font-semibold text-sm rounded-t-xl transition whitespace-nowrap"
                >
                    <BaseIcon :path="mdiBroom" :size="20" class="text-rose-600" />
                    <span>3. Mantenimiento y Caché</span>
                </button>
            </div>

            <!-- CONTENIDO TAB 1: ACTUALIZACIÓN Y DESPLIEGUE -->
            <div v-show="activeTab === 'actualizacion'" class="bg-white rounded-b-2xl border border-slate-200/80 p-6 space-y-6 shadow-sm">
                <!-- Banner de Versión y Estado -->
                <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-purple-50 via-indigo-50/50 to-white border border-purple-200/80 text-slate-800 shadow-xs">
                    <div class="flex items-center gap-4">
                        <div class="w-14 h-14 rounded-2xl bg-purple-100 text-purple-700 border border-purple-200 flex items-center justify-center shrink-0 shadow-xs">
                            <BaseIcon :path="mdiCloudUpload" :size="30" />
                        </div>
                        <div>
                            <div class="flex items-center gap-2">
                                <h3 class="text-lg font-bold text-slate-900">UNENSEDU Core</h3>
                                <span class="text-xs px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200 font-mono font-bold">
                                    v{{ currentSystemVersion }}
                                </span>
                            </div>
                            <p class="text-xs text-slate-500 mt-1 font-medium">
                                Laravel {{ laravelVersion }} &bull; PHP {{ phpVersion }} &bull; {{ osInfo }}
                            </p>
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <button
                            type="button"
                            :disabled="!isUnlocked || isCheckingRemote"
                            @click="checkRemoteERP"
                            class="px-4 py-2 bg-white hover:bg-purple-50 border border-purple-200 rounded-xl text-xs font-semibold text-purple-700 transition flex items-center gap-2 disabled:opacity-50 shadow-xs hover:border-purple-300"
                        >
                            <BaseIcon :path="mdiRefresh" :size="16" :class="{ 'animate-spin': isCheckingRemote }" />
                            <span>{{ isCheckingRemote ? 'Consultando ERP...' : 'Buscar Actualización en ERP' }}</span>
                        </button>
                    </div>
                </div>

                <!-- Respuesta de búsqueda en ERP (si existe) -->
                <div v-if="remoteCheckResult" class="p-4 rounded-xl border" :class="remoteCheckResult.has_update ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-slate-50 border-slate-200 text-slate-700'">
                    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div class="flex items-center gap-2.5">
                            <BaseIcon :path="remoteCheckResult.has_update ? mdiAlertCircle : mdiCheckCircle" :size="20" :class="remoteCheckResult.has_update ? 'text-amber-600' : 'text-emerald-600'" />
                            <div>
                                <span class="font-bold text-sm block">{{ remoteCheckResult.message }}</span>
                                <span v-if="remoteCheckResult.latest_version" class="text-xs text-slate-500 font-mono">
                                    Versión disponible: v{{ remoteCheckResult.latest_version }}
                                </span>
                            </div>
                        </div>
                        <div v-if="remoteCheckResult.has_update && remoteCheckResult.download_url">
                            <button
                                type="button"
                                :disabled="!isUnlocked || isUpdating"
                                @click="installRemoteVersion(remoteCheckResult.download_url)"
                                class="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs rounded-xl shadow-xs transition flex items-center gap-2 disabled:opacity-50"
                            >
                                <span v-if="isUpdating" class="animate-spin text-xs">●</span>
                                <span>Descargar e Instalar v{{ remoteCheckResult.latest_version }}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <!-- Sección A: Instalación Manual de ZIP -->
                    <div class="p-5 rounded-2xl border border-slate-200 bg-slate-50/50 space-y-4">
                        <div class="flex items-center gap-2 border-b border-slate-200 pb-3">
                            <BaseIcon :path="mdiCloudUpload" :size="20" class="text-purple-600" />
                            <h4 class="text-sm font-bold text-slate-800">Instalar Archivo ZIP de Producción</h4>
                        </div>

                        <p class="text-xs text-slate-600">
                            Suba el paquete comprimido generado por el desarrollador (<code class="font-mono bg-white px-1.5 py-0.5 rounded border border-slate-200 text-purple-700">unensedu_*.zip</code>).
                        </p>

                        <!-- Input File Oculto y Zona de Selección -->
                        <input
                            ref="fileInputRef"
                            type="file"
                            accept=".zip"
                            class="hidden"
                            @change="onFileChange"
                        />

                        <div
                            @click="triggerFileInput"
                            class="border-2 border-dashed border-slate-300 hover:border-purple-500 hover:bg-purple-50/30 rounded-xl p-6 text-center cursor-pointer transition flex flex-col items-center justify-center gap-2"
                        >
                            <div class="w-12 h-12 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center">
                                <BaseIcon :path="mdiCloudUpload" :size="24" />
                            </div>
                            <div v-if="selectedZipFile">
                                <span class="text-sm font-bold text-purple-900 block truncate max-w-xs">{{ selectedZipFile.name }}</span>
                                <span class="text-xs text-slate-500 font-mono">{{ (selectedZipFile.size / (1024 * 1024)).toFixed(2) }} MB</span>
                            </div>
                            <div v-else>
                                <span class="text-sm font-semibold text-slate-700 block">Haga clic para seleccionar el archivo ZIP</span>
                                <span class="text-xs text-slate-400">Archivos .zip empaquetados para producción</span>
                            </div>
                        </div>

                        <div class="flex items-center justify-between pt-2">
                            <span class="text-[11px] text-slate-500">
                                Requiere PIN de desarrollador para iniciar el despliegue.
                            </span>
                            <button
                                type="button"
                                :disabled="!isUnlocked || !selectedZipFile || isUpdating"
                                @click="executeZipUpload"
                                class="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-sm transition flex items-center gap-2 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                            >
                                <span v-if="isUpdating" class="animate-spin text-xs">●</span>
                                <span>{{ isUpdating ? 'Instalando Actualización...' : 'Instalar Actualización' }}</span>
                            </button>
                        </div>

                        <!-- Consola de Salida del Despliegue -->
                        <div v-if="updateLogs.length > 0 || updateError" class="mt-4 p-3 bg-slate-900 text-slate-100 rounded-xl font-mono text-xs max-h-48 overflow-y-auto space-y-1">
                            <div class="text-[10px] uppercase font-bold text-purple-400 border-b border-slate-800 pb-1 mb-2">
                                Registro de Despliegue:
                            </div>
                            <div v-for="(log, idx) in updateLogs" :key="idx" class="text-slate-300">
                                &gt; {{ log }}
                            </div>
                            <div v-if="updateError" class="text-rose-400 font-bold">
                                &gt; Error: {{ updateError }}
                            </div>
                        </div>
                    </div>

                    <!-- Sección B: Parámetros del ERP Central -->
                    <div class="p-5 rounded-2xl border border-slate-200 bg-slate-50/50 space-y-4">
                        <div class="flex items-center gap-2 border-b border-slate-200 pb-3">
                            <BaseIcon :path="mdiServerNetwork" :size="20" class="text-indigo-600" />
                            <h4 class="text-sm font-bold text-slate-800">Conexión con Servidor ERP Central</h4>
                        </div>

                        <p class="text-xs text-slate-600">
                            Configure la URL y la llave de autorización del ERP central para permitir consultas y recepcionar actualizaciones remotas.
                        </p>

                        <div class="space-y-3">
                            <div>
                                <label class="text-xs font-semibold text-slate-700 block mb-1">URL Endpoint del ERP</label>
                                <input
                                    type="text"
                                    :disabled="!isUnlocked"
                                    :value="getConstantValue('SYSTEM_AUTO_UPDATE_ENDPOINT')"
                                    @change="updateConstant('SYSTEM_AUTO_UPDATE_ENDPOINT', $event.target.value)"
                                    placeholder="https://erp.tu-empresa.com/api/system-updates"
                                    class="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-mono disabled:bg-slate-100"
                                />
                            </div>

                            <div>
                                <label class="text-xs font-semibold text-slate-700 block mb-1">Token de Autorización (Bearer)</label>
                                <input
                                    type="text"
                                    :disabled="!isUnlocked"
                                    :value="getConstantValue('SYSTEM_AUTO_UPDATE_TOKEN')"
                                    @change="updateConstant('SYSTEM_AUTO_UPDATE_TOKEN', $event.target.value)"
                                    placeholder="Clave secreta compartida con el ERP"
                                    class="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-mono disabled:bg-slate-100"
                                />
                            </div>

                            <div class="text-[11px] text-slate-400 pt-1">
                                Endpoints de la API local expuestos para el ERP:
                                <ul class="list-disc ml-4 mt-1 space-y-0.5 font-mono text-[10px] text-slate-600">
                                    <li>GET /api/system/version</li>
                                    <li>POST /api/system/update</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Sección C: Historial de Actualizaciones -->
                <div class="p-5 rounded-2xl border border-slate-200 bg-white space-y-4">
                    <div class="flex items-center justify-between border-b border-slate-100 pb-3">
                        <div class="flex items-center gap-2">
                            <BaseIcon :path="mdiHistory" :size="20" class="text-slate-600" />
                            <h4 class="text-sm font-bold text-slate-800">Historial de Actualizaciones Realizadas</h4>
                        </div>
                        <span class="text-xs text-slate-400 font-mono">{{ localUpdateHistory.length }} registro(s)</span>
                    </div>

                    <div v-if="localUpdateHistory.length === 0" class="text-center py-6 text-xs text-slate-400">
                        No hay registros de actualizaciones previas.
                    </div>
                    <div v-else class="overflow-x-auto">
                        <table class="w-full text-left text-xs">
                            <thead class="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                                <tr>
                                    <th class="py-2.5 px-3">Fecha</th>
                                    <th class="py-2.5 px-3">Versión</th>
                                    <th class="py-2.5 px-3">Fuente</th>
                                    <th class="py-2.5 px-3">Usuario</th>
                                    <th class="py-2.5 px-3">Estado</th>
                                    <th class="py-2.5 px-3">Detalle</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-slate-100">
                                <tr v-for="(item, idx) in localUpdateHistory" :key="idx" class="hover:bg-slate-50/50">
                                    <td class="py-2.5 px-3 text-slate-600 font-mono whitespace-nowrap">{{ item.date || item.applied_at }}</td>
                                    <td class="py-2.5 px-3 font-mono font-bold text-purple-700 whitespace-nowrap">v{{ item.version }}</td>
                                    <td class="py-2.5 px-3 text-slate-600 truncate max-w-xs" :title="item.source">{{ item.source }}</td>
                                    <td class="py-2.5 px-3 text-slate-600">{{ item.user_name || item.applied_by || 'Sistema' }}</td>
                                    <td class="py-2.5 px-3 whitespace-nowrap">
                                        <span class="px-2 py-0.5 rounded-full text-[10px] font-bold" :class="(item.status === 'success' || !item.status) ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'">
                                            {{ (item.status === 'success' || !item.status) ? 'Éxito' : 'Falló' }}
                                        </span>
                                    </td>
                                    <td class="py-2.5 px-3 text-slate-500 text-[11px] truncate max-w-sm" :title="item.message">{{ item.message || 'Actualización aplicada con éxito.' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- CONTENIDO TAB 2: PARÁMETROS DEL CORE -->
            <div v-show="activeTab === 'constantes'" class="bg-white rounded-b-2xl border border-slate-200/80 p-6 space-y-6 shadow-sm">
                <div class="border-b border-slate-100 pb-4">
                    <h4 class="text-lg font-bold text-slate-800">Constantes y Parámetros Técnicos del Sistema</h4>
                    <p class="text-xs text-slate-500">
                        Edición controlada de variables maestras del motor UNENSEDU. Se requiere PIN de desarrollador para guardar cambios.
                    </p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                        v-for="constante in localConstants"
                        :key="constante.id"
                        class="p-4 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition space-y-2"
                    >
                        <div class="flex items-center justify-between">
                            <span class="font-mono text-xs font-bold text-indigo-700 truncate" :title="constante.name">
                                {{ constante.name }}
                            </span>
                            <span class="text-[10px] px-2 py-0.5 rounded bg-slate-200 text-slate-700 font-mono">
                                {{ constante.type || 'text' }}
                            </span>
                        </div>

                        <p v-if="constante.note" class="text-[11px] text-slate-500">
                            {{ constante.note }}
                        </p>

                        <div class="flex items-center gap-2 pt-1">
                            <input
                                type="text"
                                :disabled="!isUnlocked || savingKeys[constante.name]"
                                :value="constante.value"
                                @change="updateConstant(constante.name, $event.target.value)"
                                class="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono disabled:bg-slate-100"
                            />
                            <span v-if="savingKeys[constante.name]" class="animate-spin text-indigo-600 text-xs shrink-0">●</span>
                        </div>

                        <div class="flex items-center justify-between text-[10px] text-slate-400 pt-1">
                            <span v-if="constante.updated_at">Modif: {{ constante.updated_at }}</span>
                            <span v-if="constante.user_name">por: {{ constante.user_name }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- CONTENIDO TAB 3: MANTENIMIENTO Y CACHÉ -->
            <div v-show="activeTab === 'mantenimiento'" class="bg-white rounded-b-2xl border border-slate-200/80 p-6 space-y-6 shadow-sm">
                <div class="border-b border-slate-100 pb-4">
                    <h4 class="text-lg font-bold text-slate-800">Utilidades de Mantenimiento y Rendimiento</h4>
                    <p class="text-xs text-slate-500">
                        Herramientas para purgar cachés de rutas, vistas, eventos y optimizar el rendimiento del servidor en producción.
                    </p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Limpieza de Caché -->
                    <div class="p-5 rounded-2xl border border-slate-200 bg-slate-50/50 space-y-4">
                        <div class="flex items-center gap-2 border-b border-slate-200 pb-3">
                            <BaseIcon :path="mdiBroom" :size="20" class="text-rose-600" />
                            <h5 class="text-sm font-bold text-slate-800">Limpiar Caché de la Aplicación</h5>
                        </div>
                        <p class="text-xs text-slate-600">
                            Ejecuta internamente <code class="font-mono bg-white px-1.5 py-0.5 rounded border border-slate-200 text-rose-700">php artisan optimize:clear</code>. Elimina caché de configuración, eventos, rutas y vistas compiladas de Blade/Inertia.
                        </p>

                        <button
                            type="button"
                            :disabled="!isUnlocked || isClearingCache"
                            @click="clearCache"
                            class="px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow-xs transition flex items-center gap-2 disabled:opacity-50"
                        >
                            <BaseIcon :path="mdiBroom" :size="16" :class="{ 'animate-spin': isClearingCache }" />
                            <span>{{ isClearingCache ? 'Limpiando...' : 'Ejecutar optimize:clear' }}</span>
                        </button>

                        <div v-if="cacheOutput" class="mt-3 p-3 bg-slate-900 text-emerald-400 font-mono text-xs rounded-xl whitespace-pre-wrap max-h-40 overflow-y-auto">
                            {{ cacheOutput }}
                        </div>
                    </div>

                    <!-- Comandos de Referencia para el Desarrollador -->
                    <div class="p-5 rounded-2xl border border-slate-200 bg-slate-50/50 space-y-4">
                        <div class="flex items-center gap-2 border-b border-slate-200 pb-3">
                            <BaseIcon :path="mdiCodeBrackets" :size="20" class="text-indigo-600" />
                            <h5 class="text-sm font-bold text-slate-800">Comandos de Consola Desarrollador</h5>
                        </div>
                        <ul class="text-xs space-y-2 text-slate-700 font-mono">
                            <li class="p-2 rounded bg-white border border-slate-200">
                                <span class="text-slate-400 block text-[10px]">Empaquetar versión limpia para producción (SaaS):</span>
                                <strong class="text-indigo-700">php artisan app:package {{ confGlobal.UNENSEDU_VERSION || '18.1.2' }}</strong>
                            </li>
                            <li class="p-2 rounded bg-white border border-slate-200">
                                <span class="text-slate-400 block text-[10px]">Empaquetar respaldo completo de proyecto fuente (_proy_):</span>
                                <strong class="text-indigo-700">php artisan app:package-proy {{ confGlobal.UNENSEDU_VERSION || '18.1.2' }}</strong>
                            </li>
                            <li class="p-2 rounded bg-white border border-slate-200">
                                <span class="text-slate-400 block text-[10px]">Actualizar sistema desde archivo local:</span>
                                <strong class="text-indigo-700">php artisan app:update --file=releases/unensedu_18.0.0.zip</strong>
                            </li>
                            <li class="p-2 rounded bg-white border border-slate-200">
                                <span class="text-slate-400 block text-[10px]">Ejecutar seeder de versión:</span>
                                <strong class="text-indigo-700">php artisan db:seed --class=VersionSeeder</strong>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </SectionMain>
    </BreezeAuthenticatedLayout>
</template>
